import { Plus, X } from "lucide-react";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { cn } from "@/lib/utils";

import { useTags } from "../tags/useTags";
import type { Tag } from "../types";
import { useRefreshFunnel } from "./hooks";

/** The lead's tags as small coloured labels (funnel cards, lead header) */
export const TagBadges = ({
  ids,
  className,
}: {
  ids: number[] | null | undefined;
  className?: string;
}) => {
  const { data: tags = [] } = useTags({ enabled: !!ids?.length });
  if (!ids?.length) return null;
  const shown = tags.filter((tag) => ids.includes(tag.id));
  if (!shown.length) return null;
  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {shown.map((tag) => (
        <span
          key={tag.id}
          className="rounded-md px-1.5 py-0.5 text-[11px] leading-tight font-semibold text-[#1f2a21]"
          style={{ backgroundColor: tag.color }}
        >
          {tag.name}
        </span>
      ))}
    </div>
  );
};

/** Tags of a lead with remove buttons and a picker to add one */
export const LeadTags = ({
  contactId,
  ids,
}: {
  contactId: Identifier;
  ids: number[] | null | undefined;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefreshFunnel();
  const { data: tags = [] } = useTags();
  const [adding, setAdding] = useState(false);
  const current = ids ?? [];

  const save = async (next: number[]) => {
    try {
      await dataProvider.update("contacts", {
        id: contactId,
        data: { tags: next },
        previousData: { id: contactId },
      });
      await refresh();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  const assigned = tags.filter((tag) => current.includes(tag.id));
  const free = tags.filter((tag) => !current.includes(tag.id));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {assigned.map((tag: Tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold text-[#1f2a21]"
          style={{ backgroundColor: tag.color }}
        >
          {tag.name}
          <button
            type="button"
            aria-label={translate("ra.action.remove")}
            onClick={() => save(current.filter((id) => id !== tag.id))}
            className="opacity-60 hover:opacity-100"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      {adding ? (
        <div className="flex flex-wrap gap-1">
          {free.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => {
                setAdding(false);
                save([...current, tag.id]);
              }}
              className="rounded-md border border-dashed px-2 py-0.5 text-xs font-semibold"
              style={{ backgroundColor: `${tag.color}88` }}
            >
              {tag.name}
            </button>
          ))}
          {free.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              {translate("crm.funnel.tags.none_left")}
            </span>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-0.5 rounded-md border border-dashed px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          <Plus className="size-3" />
          {translate("crm.funnel.tags.add")}
        </button>
      )}
    </div>
  );
};
